import { FieldInputStrategyRegistry } from '@/Core/Contracts/UI/IFieldInputStrategy'
import { ArrayFieldStore } from './ArrayFieldStore'
import { BaseFieldStore } from './BaseFieldStore'
import { ObjectFieldStore } from './ObjectFieldStore'
import { TableFieldStore } from './TableFieldStore'
import { FieldStrategy, FieldType, IFieldDefinition } from './Types/FieldTypeEnum'

/**
 * Фабрика для создания полей форм
 */
export class FieldStoreFactory {
    /**
     * Создать поле нужного типа
     */
    static createField(
        definition: IFieldDefinition,
        formContext: any,
        path: string[] = []
    ): BaseFieldStore {
        // Определяем стратегию если не указана
        if (!definition.strategy) {
            definition.strategy = FieldStoreFactory.inferStrategy(definition)
        }

        switch (definition.strategy) {
            case FieldStrategy.ARRAY:
                return new ArrayFieldStore(definition, formContext, path)

            case FieldStrategy.OBJECT:
                return new ObjectFieldStore(definition, formContext, path)

            case FieldStrategy.TABLE:
                return new TableFieldStore(definition, formContext, path)

            case FieldStrategy.PRIMITIVE:
            default:
                return new BaseFieldStore(definition, formContext, path)
        }
    }

    /**
     * Автоматически определить стратегию поля
     */
    static inferStrategy(definition: IFieldDefinition): FieldStrategy {
        // Якщо поле множинне (isMultiple: true), перевіряємо чи підтримує стратегія власну множинність
        if (definition.isMultiple === true) {
            // Отримуємо стратегію для типу поля
            const strategy = FieldInputStrategyRegistry.get(definition.type)
            
            // Якщо стратегія підтримує власну множинність (наприклад, SELECT, FILE),
            // залишаємо поле як PRIMITIVE
            if (strategy?.supportsNativeMultiple === true) {
                return FieldStrategy.PRIMITIVE
            }
            
            // Інші типи (STRING, NUMBER, etc.) потребують ArrayFieldStore
            return FieldStrategy.ARRAY
        }

        // Если указаны вложенные поля - это объект или таблица
        if (definition.fields) {
            // Если тип TABLE - это таблица
            if (definition.type === FieldType.TABLE) {
                return FieldStrategy.TABLE
            }
            // Если тип ARRAY и есть поля - это таблица
            if (definition.type === FieldType.ARRAY) {
                return FieldStrategy.TABLE
            }
            // Если тип OBJECT и есть поля - это объект
            if (definition.type === FieldType.OBJECT) {
                return FieldStrategy.OBJECT
            }
        }

        // Если тип TABLE без полей - все равно таблица (пустая)
        if (definition.type === FieldType.TABLE) {
            return FieldStrategy.TABLE
        }

        // Если тип ARRAY но нет полей - это массив примитивов
        if (definition.type === FieldType.ARRAY) {
            return FieldStrategy.ARRAY
        }

        // Если тип OBJECT но нет полей - это примитив (для совместимости)
        if (definition.type === FieldType.OBJECT) {
            return FieldStrategy.PRIMITIVE
        }

        // Во всех остальных случаях - примитив
        return FieldStrategy.PRIMITIVE
    }

    /**
     * Проверить, является ли поле составным (имеет вложенные поля)
     */
    static isComplexField(definition: IFieldDefinition): boolean {
        return (
            definition.strategy === FieldStrategy.OBJECT ||
            definition.strategy === FieldStrategy.TABLE
        )
    }

    /**
     * Проверить, является ли поле множественным
     */
    static isMultipleField(definition: IFieldDefinition): boolean {
        return (
            definition.strategy === FieldStrategy.ARRAY ||
            definition.strategy === FieldStrategy.TABLE
        )
    }
}
